import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, HttpClientModule, HttpResponse } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'; // Needed for Touch functionality of Material Components
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LayoutModule } from './layout/layout.module';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarConfig } from '@angular/material/snack-bar';
import { AuthenticationService } from './services/base/authentication.service';
import { ApiService } from './services/base/api.service';
import { SnackbarService } from './services/base/snackbar.service';
import { AddTokenInterceptor } from './interceptors/add-token.interceptor';
import { AddAcceptInterceptor } from './interceptors/add-accept.interceptor';
import { ApiEndpointsService } from './services/base/api-endpoints.service';
import { ReactiveFormsModule } from '@angular/forms';
import { ReportService } from './services/reports/report.service';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { LeafletMapService } from './services/base/leaflet-map.service';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular-ivy';
import { LayoutService } from './layout/layout.service';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';

import * as moment from 'moment';
import { IndexComponent } from './pages/map/index/index.component';
import { FurySharedModule } from '../@fury/fury-shared.module';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { NgxLoadingControlModule } from '@runette/ngx-leaflet-loading';
import { MarkerPopupComponent } from './pages/map/marker-popup/marker-popup.component';
import { LocalStorageService } from './services/base/local-storage.service';
import { version } from '../environments/version';
import { environment } from '../environments/environment';
import { Observable, of } from 'rxjs';
import { Module } from './interfaces/configuration/module';
import { DomainType } from './interfaces/configuration/domain-type';
import { ServerResponse } from './interfaces/base/server.response';
import { catchError, map } from 'rxjs/operators';

moment.locale('nl');

@NgModule({
  imports: [
    // Angular Core Module // Don't remove!
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    ReactiveFormsModule,

    // Fury Core Modules
    AppRoutingModule,

    // Layout Module (Sidenav, Toolbar, Quickpanel, Content)
    LayoutModule,
    FurySharedModule,
    LeafletModule,
    NgxLoadingControlModule,
  ],
  declarations: [
    AppComponent,
    IndexComponent,
    MarkerPopupComponent,
  ],
  bootstrap: [AppComponent],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {
        console.log(version + environment.appVersionAddon);
      },
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AuthenticationService, ApiEndpointsService, HttpBackend],
      multi: true,
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      }
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'fill'
      } as MatFormFieldDefaultOptions
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 5000,
        horizontalPosition: 'end',
        verticalPosition: 'bottom'
      } as MatSnackBarConfig
    },
    {
      provide: MAT_TABS_CONFIG,
      useValue: {
        animationDuration: '0',
        disablePagination: true,
        preserveContent: true,
      }
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AddTokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AddAcceptInterceptor,
      multi: true
    },
    ApiEndpointsService,
    ApiService,
    AuthenticationService,
    LocalStorageService,

    LeafletMapService,
    SnackbarService,
    ReportService,
    LayoutService,
  ]
})

export class AppModule {
}

export function initializeApp(
  authenticationService: AuthenticationService,
  apiEndpoints: ApiEndpointsService,
  httpBackend: HttpBackend,
): () => Observable<any> {
  return (): Observable<boolean> => {
    const httpApi: HttpClient = new HttpClient(httpBackend);
    return httpApi.get(environment.api_endpoint + apiEndpoints.get('public.default-organisation'), {
      observe: 'response',
      responseType: 'json',
    }).pipe(
      map((response: HttpResponse<ServerResponse>): boolean => {
        if (typeof response.body.data !== 'undefined') {
          if (response.body.data.modules.find((d: Module): boolean => d.module.name === 'reports-map') === undefined) {
            const websiteUrl: string = response.body.data.domains.find((d: DomainType): boolean => d.name === 'website');
            if (websiteUrl !== undefined) {
              return this.router.navigate([websiteUrl]).then((): void => {
              });
            }
          } else {
            authenticationService.setDefaultOrganisation(response.body.data);
            return true;
          }
        }

        window.location.href = 'https://www.soliddigital.nl/software/meldsysteem-openbare-ruimte';
        return false;
      }),
      catchError(() => {
        window.location.href = 'https://www.soliddigital.nl/software/meldsysteem-openbare-ruimte';
        return of(false);
      }
    ));
  };
}
